import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, Handle, Position, useStore, type Edge, type EdgeProps, type Node, type NodeProps } from '@xyflow/react';
import { createContext, useContext, useState, type KeyboardEvent } from 'react';
import { Popover } from '@bb/ui';
import type { CanvasValidationIssue } from '../../../api/client.ts';
import type { Step, Where } from './commands.ts';

/** What nodes and edges may do; one context instead of callbacks in node data. */
export interface EditorActions {
  readOnly: boolean;
  detailed: boolean;
  insertion: Where | null;
  pickInsert(where: Where | null): void;
  renameStep(id: string, name: string): void;
  duplicateStep(id: string): void;
  deleteStep(id: string): void;
  copyStepId(id: string): void;
  renameVariant(id: string, name: string): void;
  setVariantPercent(id: string, percent: number): void;
  cloneVariant(id: string): void;
  deleteVariant(id: string): void;
}
export const EditorActionsContext = createContext<EditorActions | null>(null);
const useActions = () => useContext(EditorActionsContext)!;

const sameWhere = (a: Where | null, b: Where) => JSON.stringify(a) === JSON.stringify(b);

/** Below this zoom nodes show only their type and a name sized to stay legible (about 11 px on screen). */
export const COMPACT_ZOOM = 0.5;
const useZoom = () => useStore((s) => s.transform[2]);
const legible = (zoom: number) => ({ fontSize: `${Math.min(48, Math.round(11 / zoom))}px`, lineHeight: 1.15 });

function InlineName({ value, label, onCommit, disabled }: { value: string; label: string; onCommit: (name: string) => void; disabled: boolean }) {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <div className="canvas-node__name" title={disabled ? undefined : 'Double-click to rename'} onDoubleClick={() => !disabled && setEditing(true)}>
        {value}
      </div>
    );
  }
  const finish = (e: { currentTarget: HTMLInputElement }, keep: boolean) => {
    if (keep) onCommit(e.currentTarget.value);
    setEditing(false);
  };
  return (
    <input
      className="nodrag canvas-node__rename"
      aria-label={label}
      defaultValue={value}
      autoFocus
      maxLength={120}
      onBlur={(e) => finish(e, true)}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') finish(e, true);
        if (e.key === 'Escape') finish(e, false);
      }}
    />
  );
}

export type StepNodeData = { step: Step; typeLabel: string; summary?: string | undefined; issues: CanvasValidationIssue[]; comments: number };
export type StepNodeType = Node<StepNodeData, 'step'>;

/** A step: type, name (double-click renames), summary in detailed view, problems, open comments, ports and a menu. */
export function StepNode({ data, selected }: NodeProps<StepNodeType>) {
  const a = useActions();
  const zoom = useZoom();
  const { step } = data;
  const errors = data.issues.filter((i) => i.severity === 'error');
  const ports = step.next;
  if (zoom < COMPACT_ZOOM) {
    return (
      <div className={`canvas-node canvas-node--compact${errors.length ? ' canvas-node--error' : ''}${selected ? ' canvas-node--selected' : ''}`} role="group" aria-label={`Step ${step.name} (${data.typeLabel})`} title={`${data.typeLabel}: ${step.name}`}>
        <Handle type="target" position={Position.Top} />
        <div className="canvas-node__compact-name" style={legible(zoom)}>
          {errors.length > 0 ? '⚠ ' : ''}
          {step.name}
        </div>
        {ports.map((n, i) => (
          <Handle key={n.port} type="source" id={n.port} position={Position.Bottom} style={{ left: `${((i + 1) / (ports.length + 1)) * 100}%` }} />
        ))}
      </div>
    );
  }
  return (
    <div className={`canvas-node${errors.length ? ' canvas-node--error' : ''}${selected ? ' canvas-node--selected' : ''}`} role="group" aria-label={`Step ${step.name} (${data.typeLabel})`}>
      <Handle type="target" position={Position.Top} />
      <div className="canvas-node__head">
        <span className="canvas-node__type">{data.typeLabel}</span>
        {errors.length > 0 && (
          <span className="canvas-node__badge canvas-node__badge--error" title={errors.map((e) => e.message).join('\n')} aria-label={`${errors.length} problems`}>
            ⚠ {errors.length}
          </span>
        )}
        {data.comments > 0 && (
          <span className="canvas-node__badge" aria-label={`${data.comments} open comments`}>
            💬 {data.comments}
          </span>
        )}
        {!a.readOnly && (
          <Popover label={`Menu of ${step.name}`} trigger={<button type="button" className="nodrag canvas-node__menu" aria-label={`Menu of ${step.name}`}>⋯</button>}>
            <div className="menu">
              <button type="button" className="bb-button bb-button--ghost" onClick={() => a.duplicateStep(step.id)}>
                Duplicate
              </button>
              <button type="button" className="bb-button bb-button--ghost" onClick={() => a.copyStepId(step.id)}>
                Copy Step ID
              </button>
              <button type="button" className="bb-button bb-button--ghost" onClick={() => a.deleteStep(step.id)}>
                Delete
              </button>
            </div>
          </Popover>
        )}
      </div>
      <InlineName value={step.name} label={`Name of ${step.name}`} disabled={a.readOnly} onCommit={(name) => a.renameStep(step.id, name)} />
      {a.detailed && data.summary && <div className="canvas-node__summary">{data.summary}</div>}
      {ports.length > 0 && (
        <div className="canvas-node__ports">
          {ports.map((n) => {
            const where: Where = { kind: 'after_step', stepId: step.id, port: n.port };
            return (
              <button key={n.port} type="button" className="nodrag canvas-port" disabled={a.readOnly} aria-pressed={sameWhere(a.insertion, where)} aria-label={`Add after ${step.name} · ${n.port}`} onClick={() => a.pickInsert(sameWhere(a.insertion, where) ? null : where)}>
                {n.port}
              </button>
            );
          })}
        </div>
      )}
      {ports.map((n, i) => (
        <Handle key={n.port} type="source" id={n.port} position={Position.Bottom} style={{ left: `${((i + 1) / (ports.length + 1)) * 100}%` }} />
      ))}
    </div>
  );
}

export type VariantNodeData = { id: string; name: string; percent: number; canDelete: boolean };
export type VariantNodeType = Node<VariantNodeData, 'variant'>;

/** A variant: name, share of entries and where its path starts. */
export function VariantNode({ data }: NodeProps<VariantNodeType>) {
  const a = useActions();
  const zoom = useZoom();
  const where: Where = { kind: 'after_variant', variantId: data.id };
  if (zoom < COMPACT_ZOOM) {
    return (
      <div className="canvas-node canvas-node--variant canvas-node--compact" role="group" aria-label={`Variant ${data.name}`}>
        <div className="canvas-node__compact-name" style={legible(zoom)}>
          {data.name} · {data.percent}%
        </div>
        <Handle type="source" id="start" position={Position.Bottom} />
      </div>
    );
  }
  return (
    <div className="canvas-node canvas-node--variant" role="group" aria-label={`Variant ${data.name}`}>
      <div className="canvas-node__head">
        <span className="canvas-node__type">Variant</span>
        <label className="nodrag canvas-node__percent">
          <input type="number" min={0} max={100} aria-label={`Share of ${data.name}`} value={data.percent} disabled={a.readOnly} onChange={(e) => a.setVariantPercent(data.id, Number(e.target.value))} />%
        </label>
        {!a.readOnly && (
          <Popover label={`Menu of ${data.name}`} trigger={<button type="button" className="nodrag canvas-node__menu" aria-label={`Menu of ${data.name}`}>⋯</button>}>
            <div className="menu">
              <button type="button" className="bb-button bb-button--ghost" onClick={() => a.cloneVariant(data.id)}>
                Clone
              </button>
              {data.canDelete && (
                <button type="button" className="bb-button bb-button--ghost" onClick={() => a.deleteVariant(data.id)}>
                  Delete
                </button>
              )}
            </div>
          </Popover>
        )}
      </div>
      <InlineName value={data.name} label={`Name of ${data.name}`} disabled={a.readOnly} onCommit={(name) => a.renameVariant(data.id, name)} />
      <div className="canvas-node__ports">
        <button type="button" className="nodrag canvas-port" disabled={a.readOnly} aria-pressed={sameWhere(a.insertion, where)} aria-label={`Add at the start of ${data.name}`} onClick={() => a.pickInsert(sameWhere(a.insertion, where) ? null : where)}>
          start
        </button>
      </div>
      <Handle type="source" id="start" position={Position.Bottom} />
    </div>
  );
}

export type LinkEdgeData = { where: Where; label: string; from: string };
export type LinkEdgeType = Edge<LinkEdgeData, 'link'>;

/** An edge with its port name and a «+» that inserts a step on it. */
export function LinkEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, markerEnd }: EdgeProps<LinkEdgeType>) {
  const a = useActions();
  const [path, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition });
  return (
    <>
      <BaseEdge id={id} path={path} {...(markerEnd ? { markerEnd } : {})} />
      <EdgeLabelRenderer>
        <div className="nodrag nopan canvas-edge__label" style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}>
          {data?.label && <span>{data.label}</span>}
          {!a.readOnly && data && (
            <button type="button" className="canvas-edge__add" aria-label={`Insert a step after ${data.from}${data.label ? ` · ${data.label}` : ''}`} aria-pressed={sameWhere(a.insertion, data.where)} onClick={() => a.pickInsert(sameWhere(a.insertion, data.where) ? null : data.where)}>
              +
            </button>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
